import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { finalize, interval, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { ActivatedRoute, Router } from '@angular/router';
import { realTimeService } from 'src/app/services/services/realtime.service';
import { NGXLogger } from 'ngx-logger';
import { SettingsService } from 'src/app/services/services/settings.service';
import { State } from 'src/app/dashboards/state';
import { Store } from '@ngrx/store';
import { TickerUsersInGroupModel, TickerUsersInGroupResponse } from 'src/app/app-models/realTimeModels';

type WindowRange = 'day' | 'week';

@Component({
    selector: 'app-ticker-user-groups-get',
    templateUrl: './ticker-user-groups-get.component.html',
    styleUrls: ['./ticker-user-groups-get.component.scss'],
    standalone: false
})
export class TickerUserGroupsGetComponent implements OnInit, OnDestroy {
  // Config / flags (wire from backend if needed)
  pollingIntervalMs = 10000;        // old: Model.AjaxRefreshInterval
  selectedWindow: WindowRange = 'day';
  showSurveyScore = true;           // old: Model.ShowSurveyScore
  hideAgentDetails = false;         // old: Model.HideAgentDetails

  // Group info
  userGroupName: string = '';
  type: string = 'ticker-user-groups';

  // Table
  displayedColumns: string[] = [
    'userName',
    'totalReceived',
    'totalAnswered',
    // (survey score column optionally included below)
    'answerRate',
    'avgSpeak',
    'avgWrapup',
    'aht',
    'timeLoggedIn',
    'timeInPause'
  ];
  displayedFooterColumns = [...this.displayedColumns];

  // Breadcrumbs
  BreadcrumbsLink = [];

  dataSource = new MatTableDataSource<TickerUsersInGroupModel>([]);
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  totals: TickerUsersInGroupModel | null = null;

  // UI state
  filter = '';
  isLoading = true;

  // Polling
  private pollingSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private realTimeService: realTimeService,
    private logger: NGXLogger,
    private store: Store<State>,
    private settingsService: SettingsService,
    private i18nextPipe: I18NextPipe,
  ) { }

  async ngOnInit(): Promise<void> {
    // Get group name from route params
    this.userGroupName = this.route.snapshot.paramMap.get('groupName') || '';

    // Set breadcrumbs for user group
    this.BreadcrumbsLink = [
      { "label": "Home", "url": "/" },
      { "label": "Real-Time", "url": "/real-time/overview" },
      { "label": "Ticker User Groups", "url": "/real-time/ticker-user-groups" },
      { "label": this.userGroupName, "url": `/real-time/ticker-user-groups/get/${this.userGroupName}` }];

    var settings = await this.settingsService.realtimeSettings();
    this.pollingIntervalMs = (settings?.refreshInterval ?? 10) * 1000;
    this.showSurveyScore = settings?.agentShowSurveyScore ?? false;
    this.hideAgentDetails = settings?.hideAgentDetails ?? true;

    // insert/remove survey score column in both header and footer
    if (this.showSurveyScore && !this.displayedColumns.includes('avgSurveyScore')) {
      const idx = this.displayedColumns.indexOf('answerRate');
      this.displayedColumns.splice(idx, 0, 'avgSurveyScore');
      const fIdx = this.displayedFooterColumns.indexOf('answerRate');
      this.displayedFooterColumns.splice(fIdx, 0, 'avgSurveyScore');
    }

    // Sorting: numeric where appropriate
    this.dataSource.sortingDataAccessor = (row: TickerUsersInGroupModel, column: string) => {
      switch (column) {
        case 'userName': return (row.valueName || '').toLowerCase();
        case 'totalReceived': return row.totalReceived ?? -1;
        case 'totalAnswered': return row.totalAnswered ?? -1;
        case 'avgSurveyScore': return row.avgSurveyScore == null ? -1 : row.avgSurveyScore;
        case 'answerRate': return row.valueAnswerRate == null ? -1 : Math.round(row.valueAnswerRate * 100);
        case 'avgSpeak': return row.secondsAvgSpeak ?? -1;
        case 'avgWrapup': return row.secondsAvgWrapup ?? -1;
        case 'aht': return row.secondsAHT ?? ((row.secondsAvgSpeak ?? 0) + (row.secondsAvgWrapup ?? 0));
        case 'timeLoggedIn': return row.secondsLoggedIn ?? -1;
        case 'timeInPause': return row.secondsInPause ?? -1;
        default: return '';
      }
    };
    this.dataSource.sort = this.sort;

    // Filter
    this.dataSource.filterPredicate = (row, filterText) => {
      const f = (filterText || '').trim().toLowerCase();
      if (!f) return true;
      return [
        row.valueName,
        String(row.totalReceived),
        String(row.totalAnswered),
        row.formattedAvgSurveyScore,
        row.formattedAnswerRate,
        row.formattedSecondsAvgSpeak,
        row.formattedSecondsAvgWrapup,
        row.formattedSecondsAHT,
        row.formattedSecondsLoggedIn,
        row.formattedSecondsInPause
      ].some(v => (v || '').toLowerCase().includes(f));
    };

    this.loadData(true);
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // --- Data load (poll) ---
  private loadData(showLoader = false): void {
    if (showLoader) this.isLoading = true;

    this.realTimeService.getTickerUsersInGroup(this.userGroupName, this.selectedWindow === 'day' ? 0 : 1)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(res => {
        this.dataSource.data = res.rows;
        this.totals = res.totals;
      });
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingSub = interval(this.pollingIntervalMs).subscribe(() => {
      this.loadData(false);
    });
  }

  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  // --- Filter handlers ---
  applyFilterFromProperty() {
    this.dataSource.filter = (this.filter || '').trim().toLowerCase();
  }

  clearFilter() {
    this.filter = '';
    this.dataSource.filter = '';
  }

  // --- Toggle window ---
  onSelectWindow(range: WindowRange) {
    if (this.selectedWindow === range) return;
    this.selectedWindow = range;
    this.loadData(true);
  }

  // --- Help ---
  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18nextPipe.transform('ticker:help_title_usersInGroup'),
        text: this.i18nextPipe.transform('ticker:help_usersInGroup'),
        renderAsHtml: false
      }
    });
  }

  formatCell(value: any, addAverage: boolean = false): string {
    if (value === undefined || value === null || value === 'NaN') {
      return this.i18nextPipe.transform('ticker:NaN');
    }
    if (addAverage) {
      return `(${this.i18nextPipe.transform('ticker:usergroups_lang_average')} ${String(value)})`;
    }
    return String(value);
  }

  // --- Navigation ---
  goBack(): void {
    this.router.navigate(['/real-time/ticker-user-groups']);
  }
}
